import {
  useGetAiSlides,
  useTemplatesIntersection,
} from "@modules/slides-library/features/template-slides/hooks";
import { SingleSlidePreview } from "@modules/slides-library/features/template-slides/components/single-slide-preview";
import { SearchResults } from "@modules/slides-library/features/template-slides/components/ai-search-results";
import {
  buildLayoutsSections,
  buildTemplatesSections,
  type OrderedSection,
} from "@modules/slides-library/features/template-slides/components/template-category-groups";
import type { TDesignSlide } from "@/shared/types";
import {
  IconClock,
  IconFiles,
  IconFlame,
  IconLibrary,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@lib/cn";
import { RECENTLY_USED_MAX } from "../constants";
import { SlidesLibraryContentSkeleton } from "./skeleton/slides-library-content-skeleton";
import { useRecentlyUsedStore, useSlidesLibraryStoreShallow } from "../store";

import { isMostPopularTitle } from "../utils/most-popular";

// Re-exported for existing importers (e.g. slides-library-dialog).
export { isMostPopularTitle };

export const SidebarIconItem = ({
  id,
  icon: IconComp,
  label,
  onClick,
  className,
}: {
  id: string;
  icon: React.ComponentType<{ size: number; stroke: number; className: string }>;
  label: string;
  onClick: (id: string) => void;
  className?: string;
}) => {
  const activeCategoryId = useSlidesLibraryStoreShallow(
    (s) => s.activeTemplateCategoryId,
  );
  const isActive = activeCategoryId === id;

  return (
    <div
      onClick={() => onClick(id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(id);
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      aria-label={label}
      data-sidebar-id={`category:${id}`}
      className={cn(
        "tw-flex tw-h-8 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-100 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-200",
        isActive && "tw-bg-blue-100",
        className,
      )}
    >
      <IconComp size={16} stroke={1} className="tw-shrink-0 tw-fill-none tw-text-blue" />
      {label}
    </div>
  );
};

// Secondary tab strip used inside both the editor's TemplatesDialog and the
// unified library dialog. Kept here so the two stay visually identical.
export type TemplatesSubTab = "single-slides" | "import-from-stories";

export const TEMPLATES_SUB_TABS: {
  value: TemplatesSubTab;
  label: string;
  icon: TablerIcon;
}[] = [
  { value: "single-slides", label: "Single slides", icon: IconLibrary },
  {
    value: "import-from-stories",
    label: "Import from stories",
    icon: IconFiles,
  },
];

export const TemplatesSubTabStrip = ({
  activeTab,
  onChange,
  className,
}: {
  activeTab: TemplatesSubTab;
  onChange: (tab: TemplatesSubTab) => void;
  className?: string;
}) => (
  <div
    className={cn(
      "tw-flex tw-items-center tw-gap-2 tw-pb-4 tw-pl-6 tw-pr-4",
      className,
    )}
  >
    {TEMPLATES_SUB_TABS.map((tab) => {
      const TabIcon = tab.icon;
      const isActive = activeTab === tab.value;
      return (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          className={cn(
            "tw-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-lg tw-border-none tw-px-3 tw-font-sans tw-text-xs tw-font-medium tw-text-blue tw-outline-none tw-transition-all tw-cursor-pointer",
            isActive
              ? "tw-bg-blue-50"
              : "tw-bg-transparent tw-opacity-50",
          )}
        >
          <TabIcon size={14} stroke={1.5} className="tw-shrink-0" />
          {tab.label}
        </button>
      );
    })}
  </div>
);

export const SectionHeader = ({
  icon: IconComp,
  label,
}: {
  icon?: React.ComponentType<{ size: number; stroke: number; className: string }>;
  label: string;
}) => (
  <div className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-4">
    {IconComp && (
      <IconComp size={16} stroke={1} className="tw-shrink-0 tw-fill-none tw-text-blue" />
    )}
    <span className="tw-font-sans tw-text-sm tw-font-medium tw-text-blue">
      {label}
    </span>
  </div>
);

const slideGridClass = "tw-flex tw-flex-wrap tw-gap-2 tw-px-6 tw-pb-4";

export const SlideGridContent = () => {
  const [render, setRender] = useState(false);
  const { data, isLoading } = useGetAiSlides();
  const { search, setIsSearching } = useSlidesLibraryStoreShallow((s) => ({
    search: s.search,
    setIsSearching: s.setIsSearching,
  }));
  const isInSearch = !!search;
  const libraryType = useSlidesLibraryStoreShallow((s) => s.type);
  const recentCategory = libraryType === "layouts" ? "layouts" : "templates";
  const recentIds = useRecentlyUsedStore((s) => s.lists[recentCategory]);

  useTemplatesIntersection({ loaded: !isLoading && render });

  useEffect(() => {
    if (!data) return;
    const id = requestAnimationFrame(() => setRender(true));
    return () => cancelAnimationFrame(id);
  }, [data]);

  const searchResults = useMemo<TDesignSlide[]>(() => {
    if (!search.trim() || !data?.slides) return [];
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const all = (Object.values(data.slides) as TDesignSlide[][]).flat();
    const seen = new Set<string>();
    return all.filter((slide) => {
      if (seen.has(slide._id)) return false;
      // Match every token across title + category, so e.g. "horizontal timeline"
      // finds a "Horizontal …" slide that lives in the Timeline category.
      const haystack =
        `${slide.title} ${slide.categoryTitle ?? ""}`.toLowerCase();
      if (!tokens.every((t) => haystack.includes(t))) return false;
      seen.add(slide._id);
      return true;
    });
  }, [search, data]);

  useEffect(() => {
    setIsSearching(false);
  }, [search, setIsSearching]);

  const recentlyUsedSlides = useMemo(() => {
    if (!data?.slides) return [];
    const byId = new Map<string, TDesignSlide>();
    const buckets = Object.values(data.slides) as TDesignSlide[][];
    for (const bucket of buckets) {
      for (const s of bucket) if (!byId.has(s._id)) byId.set(s._id, s);
    }
    return recentIds
      .map((id) => byId.get(id))
      .filter((s): s is TDesignSlide => !!s)
      .slice(0, RECENTLY_USED_MAX);
  }, [data, recentIds]);

  const orderedSections = useMemo<OrderedSection[]>(() => {
    if (!data?.slides || !data?.categories) return [];
    const slidesMap = data.slides as Record<string, TDesignSlide[]>;
    return libraryType === "templates"
      ? buildTemplatesSections(data.categories, slidesMap)
      : buildLayoutsSections(data.categories, slidesMap);
  }, [libraryType, data]);

  const mostPopularSlides = useMemo<TDesignSlide[]>(() => {
    if (!data?.slides) return [];
    const entry = Object.entries(data.slides as Record<string, TDesignSlide[]>).find(
      ([key]) => isMostPopularTitle(key),
    );
    return entry?.[1] ?? [];
  }, [data]);

  if (search) return <SearchResults data={searchResults} search={search} />;
  if (isLoading || !render || !data?.slides)
    return <SlidesLibraryContentSkeleton variant="single-slides" />;

  return (
    <div>
      {!isInSearch && recentlyUsedSlides.length > 0 && (
        <div data-category-id="category:recently-used">
          <SectionHeader icon={IconClock} label="Recently used" />
          <div className={slideGridClass}>
            {recentlyUsedSlides.map((slide: TDesignSlide) => (
              <SingleSlidePreview
                key={`recent-${slide._id}`}
                slide={slide}
                showCategoryBadge
              />
            ))}
          </div>
        </div>
      )}

      {mostPopularSlides.length > 0 && (
        <div data-category-id="category:most-popular">
          {!isInSearch && <SectionHeader icon={IconFlame} label="Most popular" />}
          <div className={slideGridClass}>
            {mostPopularSlides.map((slide: TDesignSlide) => (
              <SingleSlidePreview
                key={slide._id}
                slide={slide}
                showCategoryBadge
              />
            ))}
          </div>
        </div>
      )}

      {orderedSections.map((section) => (
        <div key={section.key} data-category-id={`category:${section.categoryId}`}>
          <SectionHeader label={section.title} />
          <div className={slideGridClass}>
            {section.slides.map((slide: TDesignSlide) => (
              <SingleSlidePreview key={slide._id} slide={slide} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
